import * as React from "react";
import { useState, useRef } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import Async from "react-select/async";
import { useSelector } from "react-redux";
import { Page, RootState } from "../../types";
import { Organization } from "../../organizations/types";
import { CreateSimpleAppointmentRequest, AppointmentWorkflowStatus, Workflow } from "../types";
import { Patient, PatientResponse } from "../../patient/types";
import { post, get } from "../../../utils/fetch";
import useOnClickOutside from "../../../utils/useOnClickOutside";
import { Link } from "react-router-dom";
import { components } from "react-select";

type Props = {
  addAppointmentToList: (queuedAppointment: AppointmentWorkflowStatus) => void;
  selectedWorkflow?: Workflow | null;
  currentAppointmentDate: Date;
  setLoading: (isLoading: boolean) => void;
};

const AddAppointment: React.FunctionComponent<Props> = ({
  addAppointmentToList,
  selectedWorkflow,
  setLoading,
  currentAppointmentDate
}: Props) => {
  const dropdownMenu = useRef();
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [patient, setPatient] = useState<string>("");
  const [appointmentDate, setAppointmentDate] = useState<Date>(currentAppointmentDate);

  const currentOrganization: Organization | null = useSelector(
    (state: RootState) => state.orgs.currentOrganization
  );

  useOnClickOutside(dropdownMenu, () => setShowMenu(false));

  const searchAllPatients = async (searchString: string) => {
    if (!searchString) return;
    // need to add support for pagination
    const url =
      "/api/organization/" +
      currentOrganization?.id +
      "/patients/?search=" +
      encodeURIComponent(searchString);

    const resp = await get<Page<PatientResponse>>(url);
    if (resp) return resp.data?.results;
    return [];
  };

  const patientSearchTemplate = (innerProps: any) => {
    return (
      <components.Option {...innerProps}>
        <div className="flex-between">
          <div>
            {innerProps.data.familyName}, {innerProps.data.givenName}
          </div>
          <div>{moment(innerProps.data.dateOfBirth).calendar()}</div>
        </div>
      </components.Option>
    );
  };

  const noOptionsMessage = (obj: { inputValue: string }) => {
    if (!obj.inputValue) {
      return null;
    }
    return <div>No results found. Click here to manually add a patient.</div>;
  };

  const setStartDate = (date: Date) => {
    setAppointmentDate(date);
  };

  // need to figure out this type...
  const selectPatient = (patient: Patient) => {
    if (patient) {
      setPatient(patient.id);
    }
  };

  const resetDialog = () => {
    setShowMenu(false);
    setPatient("");
  };

  const addAppointment = async () => {
    setLoading(true);

    const body: CreateSimpleAppointmentRequest = {
      patientId: patient,
      start: appointmentDate
        ? moment(appointmentDate)
            .seconds(0)
            .milliseconds(0)
            .toISOString()
        : ""
    };
    const url = `/api/organization/${currentOrganization?.id}/workflows/${selectedWorkflow?.id}/appointments/enqueueNewAppointment/`;
    const resp = await post<AppointmentWorkflowStatus>(url, body);
    if (resp && resp.data) {
      addAppointmentToList(resp.data);
    }
    setLoading(false);
    resetDialog();
  };

  const toggleShowMenu = () => {
    setPatient("");
    setAppointmentDate(null);
    setShowMenu(!showMenu);
  };

  const setDefaultDate = () => {
    if (!appointmentDate) setAppointmentDate(currentAppointmentDate);
  };

  return (
    <div className="add-patient-apt-container" ref={dropdownMenu}>
      <button onClick={toggleShowMenu} className={showMenu ? "selected" : ""}>
        <i className="far fa-plus-circle btn-icon" /> Add Patient Appointment
      </button>

      {showMenu && (
        <div className="dialog-menu">
          <div className="dialog-content">
            <label htmlFor="existing-patient">
              <div className="flex-between-center">
                <div>Select Existing Patient</div>
                <Link to="/patient/add/">
                  <button className="link">
                    <i className="far fa-plus-circle btn-icon" />
                    New Patient
                  </button>
                </Link>
              </div>
            </label>
            <div className="flex-between-center mb-20">
              <Async
                cacheOptions
                id="existing-patient"
                className="full-width"
                noOptionsMessage={noOptionsMessage}
                loadOptions={searchAllPatients}
                placeholder={"Search for a patient by name"}
                onChange={e => selectPatient(e)}
                components={{
                  Option: patientSearchTemplate,
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null
                }}
                getOptionLabel={option => option.familyName + ", " + option.givenName}
              />
            </div>
            <div className="mt-20">
              <label htmlFor="apt-date">Appointment Time</label>
            </div>
            <div className="full-width">
              <DatePicker
                id="apt-date"
                selected={appointmentDate}
                onFocus={setDefaultDate}
                onChange={(date: Date) => setStartDate(date)}
                showTimeSelect
                timeIntervals={30}
                timeCaption="Time"
                dateFormat="eeee - MMMM d, yyyy - h:mm aa"
              />
            </div>

            <div className="flex-end mt-15">
              <button onClick={addAppointment} disabled={!(patient && appointmentDate)}>
                Add Appointment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAppointment;
